import React, { useState, useRef, useEffect } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { withFallback } from "vike-react-query";
import { useSelector } from "react-redux";
import pluralize from "pluralize";
import api from "#utils/api";
import FeedCard from "#cards/FeedCard";
import CommentForm from "../forum/showPost/CommentForm";
import Comment from "../forum/showPost/Comment";
import { getMaterialIcon } from "#utils/icons";
const FavoriteBorderOutlinedIcon = getMaterialIcon("FavoriteBorderOutlined");
const FavoriteOutlinedIcon = getMaterialIcon("FavoriteOutlined");
const ExpandMoreOutlinedIcon = getMaterialIcon("ExpandMoreOutlined");

const FullActivityItem = ({ activityItemId, setCardActivityItem, commentId }) => {
  const { currentUserId } = useSelector((state) => state.user);

  // State for managing paginated comments
  const [comments, setComments] = useState([]);
  const [moreComments, setMoreComments] = useState(false);
  const [loading, setLoading] = useState(false);
  // Keep track of current page for pagination
  const pageRef = useRef(1);

  // Fetch initial activity item data with comments
  // If commentId is provided, it will highlight that specific comment
  const { data, refetch: refetchActivityItem } = useSuspenseQuery({
    queryKey: ["activityItem", activityItemId],
    queryFn: () => api.get(`/feed/activity_item/${activityItemId}${commentId ? `?highlightId=${commentId}` : ""}`),
    retry: false,
  });

  const activityItem = data.activity_item;

  // Set up initial comments and check if there are more pages to load
  useEffect(() => {
    if (data) {
      // Set initial comments from the API response
      setComments(data.comments);
      // If there's more than one page, enable the "View More" button
      if (data.total_pages > 1) {
        setMoreComments(true);
      }
    }
  }, [data]);

  const handleCommentSuccess = (data) => {
    refetchActivityItem();
    setCardActivityItem && setCardActivityItem(data.resource);
  };

  const handleLike = (e) => {
    e.stopPropagation();
    api.post(`/feed/like`, { id: activityItem.id })
      .then(() => {
        refetchActivityItem();
        setCardActivityItem && setCardActivityItem((prevState) => ({ ...prevState, is_liked: true, upvotes: prevState.upvotes + 1 }));
      });
  };

  const handleRemoveLike = (e) => {
    e.stopPropagation();
    api.post(`/feed/remove_like`, { id: activityItem.id })
      .then(() => {
        refetchActivityItem();
        setCardActivityItem && setCardActivityItem((prevState) => ({ ...prevState, is_liked: false, upvotes: prevState.upvotes - 1 }));
      });
  };

  // Load more comments when user clicks "View More"
  const handleLoadMore = () => {
    setLoading(true);
    // Increment the page number
    pageRef.current += 1;

    // Fetch the next page of comments
    api.get(`/feed/activity_item/${activityItemId}?page=${pageRef.current}`)
      .then((data) => {
        // Append new comments to existing ones
        setComments((prevState) => [...prevState, ...data.comments]);
        // Hide "View More" button if we've reached the last page
        if (data.total_pages <= pageRef.current) setMoreComments(false);
      })
      .finally(() => setLoading(false));
  };

  const renderLikeButton = () => {
    return (
      <div className="flex gap-1 self-start items-center justify-center rounded-default h-[26px] px-2.5 min-w-[51px] bg-transparent">
        <button
          className="flex justify-start items-center"
          onClick={activityItem.is_liked ? handleRemoveLike : handleLike}
          disabled={activityItem.user_id === currentUserId}
        >
          {activityItem.is_liked ? (
            <FavoriteOutlinedIcon color="#FF695F" fontSize="small" className="text-red" />
          ) : (
            <FavoriteBorderOutlinedIcon
              color="#D2D2D2"
              fontSize="small"
              className={`text-gray ${activityItem.user_id !== currentUserId && "hover:text-red"}`}
            />
          )}
        </button>
        <div className="text-xs text-center text-gray">{activityItem.upvotes}</div>
      </div>
    );
  };

  return (
    <div className="container max-w-[856px] m-auto text-left pt-1 md:p-2">
      <FeedCard activityItemData={activityItem} showReply={false} />
      <div className="flex justify-between items-center mt-4 md:mt-6 mb-1">
        {renderLikeButton()}
        <div className="text-gray text-xs">{pluralize("comments", activityItem.comment_count || 0, true)}</div>
      </div>
      {/* Only show comments section if there are comments */}
      {comments?.length > 0 && (
        <div className="mb-4 md:mb-6">
          <hr className="text-gray" />
          {/* Render each comment with optional highlight */}
          {comments.map((c) => (
            <Comment key={c.uuid} commentData={c} highlightId={commentId} />
          ))}
          {/* Show "View More" button if there are more comments to load */}
          {moreComments && (
            <button className="bg-transparent p-2 self-center mt-3 text-sm w-full" disabled={loading} onClick={handleLoadMore}>
              {loading ? (
                "Loading..."
              ) : (
                <>
                  View More <ExpandMoreOutlinedIcon fontSize="small" />
                </>
              )}
            </button>
          )}
        </div>
      )}
      <CommentForm resourceType="ActivityItem" resourceId={activityItemId} onSuccess={handleCommentSuccess} />
    </div>
  );
};

export default withFallback(FullActivityItem, undefined, () => <div>Whoops! We couldn't find that.</div>);
